import { useSyncExternalStore } from "react";

// Starred Fishbowl locations, per browser (Devon, 2026-10-13: localStorage only, no
// server column yet). The destination picker in the move modal lists them first, and
// the coming locations view will too. Same envelope idea as inboxPrefs: one key, a
// tiny subscriber list, and a whitelist entry in preservingInboxPrefs so signOut's
// localStorage.clear() does not wipe them.

export const FISHBOWL_LOCATION_FAVORITES_KEY = "fishbowl_location_favorites";

const listeners = new Set<() => void>();
let cached: number[] | null = null;

function normalize(raw: unknown): number[] {
    if (!Array.isArray(raw)) return [];
    const ids: number[] = [];
    for (const value of raw) {
        const id = Number(value);
        if (Number.isInteger(id) && id > 0 && !ids.includes(id)) ids.push(id);
    }
    return ids;
}

export function readFavoriteLocationIds(): number[] {
    if (cached) return cached;
    try {
        cached = normalize(JSON.parse(localStorage.getItem(FISHBOWL_LOCATION_FAVORITES_KEY) || "[]"));
    } catch {
        cached = [];
    }
    return cached;
}

function write(ids: number[]) {
    cached = ids;
    try {
        localStorage.setItem(FISHBOWL_LOCATION_FAVORITES_KEY, JSON.stringify(ids));
    } catch {
        // Private mode or blocked storage: the star still works for this page view.
    }
    listeners.forEach((fn) => fn());
}

export function isFavoriteLocation(id: number): boolean {
    return readFavoriteLocationIds().includes(id);
}

/** Stars or unstars one location and returns the new list. */
export function toggleFavoriteLocation(id: number): number[] {
    const current = readFavoriteLocationIds();
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    write(next);
    return next;
}

function subscribe(fn: () => void) {
    listeners.add(fn);
    const onStorage = (e: StorageEvent) => {
        if (e.key === FISHBOWL_LOCATION_FAVORITES_KEY || e.key === null) {
            cached = null;
            fn();
        }
    };
    window.addEventListener("storage", onStorage);
    return () => {
        listeners.delete(fn);
        window.removeEventListener("storage", onStorage);
    };
}

/** The starred location ids and a toggle, re-rendering on change in any tab. */
export function useFavoriteLocations(): [number[], (id: number) => void] {
    const ids = useSyncExternalStore(subscribe, readFavoriteLocationIds, () => []);
    return [ids, toggleFavoriteLocation];
}

/** Test seam: forget the in-memory copy so the next read hits localStorage. */
export function resetFavoriteLocationsCache() {
    cached = null;
}
